/**
 * Endpoints whose payloads are encrypted, and which scheme each one uses.
 *
 * Kept in its own module so both baseQuery and secureTransport can import it
 * without a circular dependency.
 */

/** Endpoints encrypted with the legacy static key (the original `wantedURLs`). */
export const WANTED_URLS = [
  '/product/life/get-send-consentlink/',
  '/policy/sp-consent',
  '/policy/motor-constent',
  '/policy/motor-constent/submit',
  '/policy/sp-consent/submit',
  '/policy/fetch-vahan-details',
  '/policy/proposal',
  '/policy/send_otp',
  '/policy/validate_otp',
  '/product/life/save-suitability',
  '/product/life/plan-quote',
  '/product/life/create-proposal',
  '/product/life/proposal/submit-nominee',
  '/product/life/save-ecs-data',
  '/product/life/proposal-submission-to-insurer',
  '/product/life/disability-consent-send-link',
  '/customer/getCBSCustomer',
  '/customer/store-cbs-customer',
  '/common/pincode',
  '/common/quotation',
  '/common/list-miscellaneous',
  '/common/check-bmi',
  '/common/list-suminsured',
  '/common/health-constent-generate',
  '/common/health-constent-check',
  '/common/health-constent-resend',
  '/common/send-otp',
  '/common/validate-otp',
  '/common/health-constent-submit',
  '/common/question',
  '/common/proposal',

  // ================= Insurer (health prefix) =================
  '/insurer/list-insurer',
  '/insurer/policy-status',
  '/insurer/list-benifits',
  '/insurer/list-cash-less-hospitals',
  '/insurer/manual-policy-fetch',
  '/insurer/insurer-and-insurance-details-for-endorsement',
  '/insurer/list-insurer-plans',
  '/insurer/compare-insurer-plans',
  '/insurer/benefits-map',
  '/insurer/getinsurancelist',
  '/insurer/updateinsurancestatus',
  '/common/check-ckyc',
  '/common/validate-ckyc-insurer',

  // ================= IDK where they are used (health prefix) =================
  '/common/list-proposal-fields',
  '/common/list-riders',
  '/common/list-plantypes',
  '/common/fetch_user_data',
  '/common/sales_report',
  '/common/dms-data-push',
  '/common/validate-ckyc-insurer',
  '/common/check-ckyc',
  '/common/offline-upload',
  '/common/bulk-offline-upload',
  '/common/list-deductibles',
  '/common/save-cbs-bank-data',
  '/common/proposal-details-for-endorsement',
  '/common/insurer-pincode-api',
  '/common/list-policy-compare-fields',
  '/common/policy-compare',
  '/common/health-get-constent-from-dms',
  '/common/fetch-log',

  '/customer-360/policy-listing',

  // ================= PAYMENT (health prefix) =================
  '/health/payment',
  '/health/payment/bank-direct-debit',
  '/health/payment/check',
  '/health/payment/insurer-side-payment-check',
  '/health/callback',
]

export type TSecureEnvelope = 'encrypted_data' | 'request_data' | 'data'

/**
 * Endpoints migrated to session-key encryption (RSA-OAEP + AES-256-GCM), and the
 * envelope each uses:
 *
 *   'encrypted_data' — baseQuery wraps the body and unwraps the response.
 *   'request_data'   — the CALL SITE encrypts/decrypts; baseQuery only attaches
 *                      the `x-session-key` header.
 *
 * NOTE: the server picks its scheme purely on the presence of `x-session-key`.
 * A service that has not implemented the middleware ignores the header and tries
 * the static key against a session-key blob, which fails. Per backend guide §9,
 * foundation is the only service implemented; the WANTED_URLS below are hosted by
 * bob-cms, bob-product, bob-lms, health, customer and customer-360 and will fail
 * until each ships its side. Gated to non-PROD by `isSecureEnabled()`.
 *
 * NOT eligible (backend round-2): /accounts/SendOtp (no support),
 * /accounts/VerifyOtp (AES-128-ECB, unchanged this phase).
 */
export const SECURE_URLS: Record<string, TSecureEnvelope> = {
  '/accounts/login': 'request_data',
  '/accounts/verify-otp': 'request_data',

  // LMS — {"data": "<blob>", "is_encrypted": 1} in, {"encrypted_data": "<blob>"} out.
  // Encrypted at the call site via encryptMessage/decryptMessage in Utils, so
  // baseQuery only attaches the header.
  '/lms/v1/create-lead': 'data',
  '/lms/v1/update-lead': 'data',
  '/lms/v1/leads/dashboard': 'data',
  '/lms/v1/leads/analytics': 'data',
  // NOTE: /lms/v1/leadcount is NOT encrypted — plain body, leave it alone.

  // Field-level encryption: the body is plain JSON with ONE encrypted field
  // (`otp`), so baseQuery must not wrap it — it only attaches the header.
  '/product/life/verify-otp': 'data', // SP approval OTP (Acr)
  '/product/life/declaration-verify-otp': 'data', // vernacular declaration OTP

  // Bank-login OTP. The response is decrypted with the session key in
  // useBankLogin. Backend has NOT migrated this endpoint yet (their round-2
  // answer: still AES-128-ECB), so it fails until they do — but the header is
  // sent so it starts working the moment they ship.
  '/accounts/VerifyOtp': 'data',
  '/accounts/fetchspuserdatalist': 'encrypted_data',
}

WANTED_URLS.forEach((url) => {
  SECURE_URLS[url] = 'encrypted_data'
})
